import { useCallback, useEffect, useState } from 'react';
import { AllowanceFormModal } from './AllowanceFormModal';
import { salaryConfigApi, type SalaryAllowance, type SalaryConfigData } from '../lib/api/salary-config';
import { formatVND } from '../lib/format';
import { useToast } from '../lib/use-toast';
import { TableActionMenu } from './TableActionMenu';

const PIT_BRACKETS = [
  { bac: 1, range: 'Đến 5 triệu', rate: '5%' },
  { bac: 2, range: '5 – 10 triệu', rate: '10%' },
  { bac: 3, range: '10 – 18 triệu', rate: '15%' },
  { bac: 4, range: '18 – 32 triệu', rate: '20%' },
  { bac: 5, range: '32 – 52 triệu', rate: '25%' },
  { bac: 6, range: '52 – 80 triệu', rate: '30%' },
  { bac: 7, range: 'Trên 80 triệu', rate: '35%' },
];

export function SalaryFormulaSection() {
  const { toast, showToast } = useToast();
  const [config, setConfig] = useState<SalaryConfigData | null>(null);
  const [allowances, setAllowances] = useState<SalaryAllowance[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [editingParam, setEditingParam] = useState<string | null>(null);
  const [allowanceModalOpen, setAllowanceModalOpen] = useState(false);
  const [editingAllowance, setEditingAllowance] = useState<SalaryAllowance | null>(null);
  const [allowanceMenuId, setAllowanceMenuId] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true); setErr(null);
    try {
      const r = await salaryConfigApi.get();
      setConfig(r.config); setAllowances(r.allowances);
    } catch (e) { setErr(e instanceof Error ? e.message : String(e)); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const saveParam = async (field: keyof SalaryConfigData, value: number | string) => {
    if (!config) return;
    try {
      await salaryConfigApi.patchConfig({ [field]: value });
      showToast(`Đã cập nhật ${field}`);
      setEditingParam(null);
      load();
    } catch (e) { showToast(e instanceof Error ? e.message : String(e)); }
  };

  const toggleActive = async (a: SalaryAllowance) => {
    try {
      await salaryConfigApi.patchAllowance(a.id, { active: !a.active });
      load();
    } catch (e) { showToast(e instanceof Error ? e.message : String(e)); }
  };

  if (loading) return <div className="pay-empty">Đang tải…</div>;
  if (err) return <div className="pay-table-error">Lỗi: {err}</div>;
  if (!config) return <div className="pay-empty">Chưa có cấu hình lương.</div>;

  return (
    <>
      <section className="sal-hero">
        <div className="sal-head-main">
          <div className="sal-hero-icon" aria-hidden="true">
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect width="16" height="18" x="4" y="3" rx="2" /><path d="M8 7h8M8 11h8M8 15h4" /></svg>
          </div>
          <div>
            <div className="sal-head-label">Công thức tính lương áp dụng</div>
            <div className="sal-head-title-row">
              <h2>Chi phí lương &amp; Bảo hiểm</h2>
              <span className="sal-version-pill">{config.version}</span>
            </div>
            <p>Cập nhật {new Date(config.effectiveFrom).toLocaleDateString('vi-VN')}{config.updatedBy && ` · ${config.updatedBy}`}{config.notes && ` · ${config.notes}`}</p>
          </div>
        </div>
        <div className="sal-head-warn">
          <div className="sal-head-warn-icon"><svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" /><path d="M12 9v4M12 17h.01" /></svg></div>
          <div>
            <b>Cần đối chiếu công thức</b>
            <p>Cập nhật khi có luật thuế mới (NĐ, TT). Version hiện tại: <b>{config.version}</b>.</p>
          </div>
        </div>
      </section>

      <div className="sal-card">
        <div className="sal-card-head">
          <h3>Tham số áp dụng</h3>
        </div>
        <div className="sal-param-grid">
          <ParamCard label="Ngày công chuẩn" value={`${config.workingDaysStandard} ngày`} onEdit={() => setEditingParam('workingDaysStandard')} />
          <ParamCard label="LTT vùng I" value={formatVND(config.minWageRegion1)} onEdit={() => setEditingParam('minWageRegion1')} />
          <ParamCard label="Giảm trừ bản thân" value={formatVND(config.personalDeduct)} onEdit={() => setEditingParam('personalDeduct')} />
          <ParamCard label="Giảm trừ NPT" value={`${formatVND(config.dependentDeduct)}/người`} onEdit={() => setEditingParam('dependentDeduct')} />
        </div>
      </div>

      <div className="sal-card">
        <div className="sal-card-head">
          <div>
            <h3>Loại phụ cấp cố định</h3>
            <p className="sal-card-sub">{allowances.length} loại · {allowances.filter((a) => a.active).length} đang bật</p>
          </div>
          <button className="pay-btn-dark" type="button" onClick={() => { setEditingAllowance(null); setAllowanceModalOpen(true); }}>+ Thêm phụ cấp</button>
        </div>
        <table className="sal-tbl">
          <thead>
            <tr>
              <th>Mã</th><th>Tên phụ cấp</th><th>Miễn thuế</th><th>Đóng BH</th><th>Mức gợi ý mặc định</th><th>Trạng thái</th><th />
            </tr>
          </thead>
          <tbody>
            {allowances.map((a) => (
              <tr key={a.id}>
                <td><span className="coa-code-pill">{a.code}</span></td>
                <td className="sal-name">{a.name}</td>
                <td><TagYesNo v={a.taxFreeLabel} /></td>
                <td><TagYesNo v={a.bhLabel} /></td>
                <td className="sal-mono">{a.suggestedLabel}</td>
                <td>
                  <span className={`coa-status-pill ${a.active ? 'on' : 'off'}`}>
                    {a.active ? 'Đang bật' : 'Tắt'}
                  </span>
                </td>
                <td className="col-act">
                  <TableActionMenu
                    open={allowanceMenuId === a.id}
                    onToggle={() => setAllowanceMenuId((current) => current === a.id ? null : a.id)}
                    onClose={() => setAllowanceMenuId(null)}
                    ariaLabel={`Thao tác phụ cấp ${a.name}`}
                    items={[
                      { label: 'Sửa phụ cấp', onClick: () => { setEditingAllowance(a); setAllowanceModalOpen(true); } },
                      { label: a.active ? 'Tắt phụ cấp' : 'Bật phụ cấp', onClick: () => toggleActive(a) },
                    ]}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <div className="sal-tbl-hint">
          Phụ cấp <b>miễn thuế</b> không tính vào TN chịu thuế TNCN. Phụ cấp <b>đóng BH</b> tính vào lương làm căn cứ đóng BHXH/BHYT/BHTN.
        </div>
      </div>

      {/* Formula sections — display-only */}
      <div className="sal-form-grid">
        <div className="sal-form-block a">
          <div className="sal-form-badge">A</div>
          <div className="sal-form-head"><h3>Thu nhập trong kỳ</h3><span>GROSS</span></div>
          <ol className="sal-formulas">
            <li><b>(1)</b> Ngày OT = Số giờ OT × 24/8 × 1.5</li>
            <li><b>(2)</b> TDCT = Ngày đi làm + (1)</li>
            <li><b>(3)</b> Lương/ngày = Lương thoả thuận ÷ {config.workingDaysStandard} ngày</li>
            <li><b>(4)</b> TN lương = (3) × (2) × Mức hưởng %</li>
            <li className="sal-formula-final"><b>(A)</b> = (4) + PC miễn thuế + PC không miễn thuế + Thưởng</li>
          </ol>
        </div>
        <div className="sal-form-block b">
          <div className="sal-form-badge">B</div>
          <div className="sal-form-head"><h3>Bảo hiểm &amp; Thuế TNCN</h3><span>Nhân viên chịu</span></div>
          <ol className="sal-formulas">
            <li><b>(5)</b> L_BH = MIN(Lương, 20 × {formatVND(config.minWageRegion1)}) — trần <b>{formatVND(20 * config.minWageRegion1)}</b></li>
          </ol>
          <div className="sal-mini-grid">
            <div className="sal-mini-box"><b>BHXH</b><span>8%</span></div>
            <div className="sal-mini-box"><b>BHYT</b><span>1.5%</span></div>
            <div className="sal-mini-box"><b>BHTN</b><span>1%</span></div>
          </div>
          <ol className="sal-formulas" start={6}>
            <li><b>(6)</b> TN tính thuế = A − BH NV − {formatVND(config.personalDeduct)} − ({formatVND(config.dependentDeduct)} × NPT)</li>
          </ol>
          <div className="sal-pit">
            <div className="sal-pit-head">(B) Biểu thuế TNCN 7 bậc lũy tiến</div>
            <table>
              <thead><tr><th>Bậc</th><th>TN tính thuế/tháng</th><th>Thuế suất</th></tr></thead>
              <tbody>
                {PIT_BRACKETS.map((b) => (
                  <tr key={b.bac}><td>{b.bac}</td><td>{b.range}</td><td><b>{b.rate}</b></td></tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        <div className="sal-form-block c">
          <div className="sal-form-badge">C</div>
          <div className="sal-form-head"><h3>Công ty đóng</h3><span>Chi phí DN</span></div>
          <div className="sal-mini-grid four">
            <div className="sal-mini-box"><b>BHXH</b><span>17%</span></div>
            <div className="sal-mini-box"><b>BHYT</b><span>3%</span></div>
            <div className="sal-mini-box"><b>BHTN</b><span>1%</span></div>
            <div className="sal-mini-box"><b>KPCĐ</b><span>2%</span></div>
          </div>
          <div className="sal-formula-final sal-total-c">(C) Tổng công ty đóng = L_BH × <b>23%</b></div>
        </div>
        <div className="sal-form-block d">
          <div className="sal-form-badge">D</div>
          <div className="sal-form-head"><h3>Thực nhận (Net)</h3><span>Chuyển khoản NV</span></div>
          <div className="sal-formula-final sal-total-d">(D) = A − BH NV − TNCN + Thưởng khác − Tạm ứng</div>
          <div className="sal-warn-note">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10" /><path d="M12 8v4M12 16h.01" /></svg>
            Kiểm tra lại công thức khi có luật thuế mới.
          </div>
        </div>
      </div>

      <AllowanceFormModal
        open={allowanceModalOpen}
        editing={editingAllowance}
        onClose={() => { setAllowanceModalOpen(false); setEditingAllowance(null); }}
        onSaved={() => {
          const wasEdit = editingAllowance !== null;
          setAllowanceModalOpen(false); setEditingAllowance(null);
          showToast(wasEdit ? 'Đã cập nhật phụ cấp' : 'Đã thêm phụ cấp');
          load();
        }}
      />

      <SalaryParameterModal
        field={editingParam as SalaryParameterField | null}
        config={config}
        onClose={() => setEditingParam(null)}
        onSave={saveParam}
      />

      {toast && <div className="coa-toast">{toast}</div>}
    </>
  );
}

type SalaryParameterField = 'workingDaysStandard' | 'minWageRegion1' | 'personalDeduct' | 'dependentDeduct';

const PARAMETER_META: Record<SalaryParameterField, { label: string; helper: string; unit: string; min: number }> = {
  workingDaysStandard: { label: 'Ngày công chuẩn', helper: 'Dùng để quy đổi lương theo ngày công trong kỳ.', unit: 'ngày', min: 1 },
  minWageRegion1: { label: 'Lương tối thiểu vùng I', helper: 'Dùng để xác định trần đóng bảo hiểm xã hội.', unit: 'đ', min: 1 },
  personalDeduct: { label: 'Giảm trừ bản thân', helper: 'Khoản giảm trừ thuế TNCN theo tháng.', unit: 'đ/tháng', min: 1 },
  dependentDeduct: { label: 'Giảm trừ người phụ thuộc', helper: 'Khoản giảm trừ thuế TNCN cho mỗi người phụ thuộc.', unit: 'đ/người/tháng', min: 1 },
};

function SalaryParameterModal({ field, config, onClose, onSave }: {
  field: SalaryParameterField | null;
  config: SalaryConfigData;
  onClose: () => void;
  onSave: (field: keyof SalaryConfigData, value: number) => Promise<void>;
}) {
  const [value, setValue] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (field) setValue(String(config[field]));
  }, [field, config]);

  if (!field) return null;
  const meta = PARAMETER_META[field];
  const numericValue = Number(value.replace(/\D/g, ''));
  const valid = Number.isFinite(numericValue) && numericValue >= meta.min;

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!valid) return;
    setSaving(true);
    try {
      await onSave(field, numericValue);
      onClose();
    } finally { setSaving(false); }
  };

  return (
    <div className="sal-modal-backdrop" onClick={onClose}>
      <form className="sal-param-modal" onSubmit={submit} onClick={(event) => event.stopPropagation()}>
        <div className="sal-param-modal-head">
          <div>
            <p>CẬP NHẬT THAM SỐ</p>
            <h3>{meta.label}</h3>
          </div>
          <button type="button" className="sal-modal-close" onClick={onClose} aria-label="Đóng">×</button>
        </div>
        <div className="sal-param-modal-body">
          <label htmlFor="salary-param-value">Giá trị áp dụng <span>*</span></label>
          <div className="sal-param-input-wrap">
            <input id="salary-param-value" autoFocus inputMode="numeric" value={value ? Number(value.replace(/\D/g, '')).toLocaleString('vi-VN') : ''} onChange={(event) => setValue(event.target.value)} />
            <span>{meta.unit}</span>
          </div>
          <p>{meta.helper}</p>
          {!valid && <div className="sal-param-error">Vui lòng nhập giá trị hợp lệ.</div>}
        </div>
        <div className="sal-param-modal-foot">
          <button type="button" className="sal-btn-secondary" onClick={onClose} disabled={saving}>Huỷ</button>
          <button type="submit" className="pay-btn-dark" disabled={!valid || saving}>{saving ? 'Đang lưu…' : 'Lưu thay đổi'}</button>
        </div>
      </form>
    </div>
  );
}

function ParamCard({ label, value, onEdit }: { label: string; value: string; onEdit: () => void }) {
  return (
    <div className="sal-param" onClick={onEdit} style={{ cursor: 'pointer' }} title="Click để sửa">
      <div className="sal-param-label">{label}</div>
      <div className="sal-param-value">{value}</div>
      <div className="sal-param-hint">Click để chỉnh</div>
    </div>
  );
}

function TagYesNo({ v }: { v: string }) {
  const lower = v.toLowerCase();
  const cls = lower.startsWith('có') ? 'yes' : lower.startsWith('không') ? 'no' : 'neutral';
  return <span className={`sal-tag ${cls}`}>{v}</span>;
}
